(() => {
  "use strict";

  // This adapter deliberately uses only Klaviyo's public browser surfaces.
  // A private API key or server credential must never be added to a theme asset.
  const PUBLIC_SITE_ID = "TmXShf";
  const MARKETING_LIST_ID = "YxRKST";
  const API_REVISION = "2026-07-15";
  const SCHEMA_VERSION = "3.0";
  const STORAGE_PREFIX = "rd-fit-klaviyo-v3:";
  const RETRY_TTL_MS = 30 * 60 * 1000;
  const REQUEST_TIMEOUT_MS = 8000;
  const SUPPORTED_EVENTS = new Set([
    "Raw Fit Quiz Completed",
    "Raw Fit Plan Requested",
    "Raw Fit SMS Opt-In",
  ]);

  function isObject(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
  }

  function own(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  function text(value, maxLength = 160) {
    if (typeof value !== "string") return "";
    return value.trim().slice(0, maxLength);
  }

  function displayText(value, maxLength = 120) {
    const result = text(value, maxLength);
    return result || null;
  }

  function code(value, maxLength = 80) {
    const result = text(value, maxLength).toLowerCase().replace(/\s+/g, "_");
    return /^[a-z0-9][a-z0-9_:-]*$/.test(result) ? result : null;
  }

  function finiteNumber(value, min = 0, max = 100000) {
    if (value === null || value === undefined || value === "") return null;
    const number = typeof value === "number" ? value : Number(value);
    return Number.isFinite(number) && number >= min && number <= max ? number : null;
  }

  function unique(values) {
    return Array.from(new Set(values.filter(Boolean)));
  }

  function codeList(value, maxItems = 12) {
    if (!Array.isArray(value)) return [];
    return unique(value.slice(0, maxItems).map((item) => code(item)));
  }

  function displayList(value, maxItems = 12, maxLength = 180) {
    if (!Array.isArray(value)) return [];
    return unique(value.slice(0, maxItems).map((item) => displayText(item, maxLength)));
  }

  function normalizedBreedNotices(value) {
    if (!Array.isArray(value)) return [];
    return value.slice(0, 4).filter(isObject).map((notice) => omitNull({
      id: code(notice.id),
      title: displayText(notice.title, 140),
      message: displayText(notice.message, 800),
    })).filter((notice) => notice.id && notice.title && notice.message);
  }

  function normalizedTransitionPlan(value) {
    if (!isObject(value)) return null;
    return omitNull({
      kind: code(value.kind),
      title: displayText(value.title, 140),
      message: displayText(value.message, 800),
      stages: (Array.isArray(value.stages) ? value.stages : []).slice(0, 4).filter(isObject).map((stage) => omitNull({
        days: displayText(stage.days, 60),
        percent: finiteNumber(stage.percent, 0, 100),
      })).filter((stage) => stage.days && stage.percent !== undefined),
    });
  }

  function firstValue(sources, keys) {
    for (const source of sources) {
      if (!isObject(source)) continue;
      for (const key of keys) {
        if (own(source, key) && source[key] !== null && source[key] !== undefined && source[key] !== "") return source[key];
      }
    }
    return null;
  }

  function exactIsoDate(value) {
    const candidate = text(value, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(candidate) ? candidate : null;
  }

  function timestamp(value) {
    const candidate = text(value, 40);
    return candidate && !Number.isNaN(Date.parse(candidate)) ? candidate : null;
  }

  function normalizeEmail(value) {
    const email = text(value, 254).toLowerCase();
    return /^\S+@\S+\.\S+$/.test(email) ? email : null;
  }

  function normalizePhone(value) {
    const phone = text(value, 20).replace(/[\s().-]/g, "");
    return /^\+[1-9]\d{7,14}$/.test(phone) ? phone : null;
  }

  function isUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }

  function providerError(codeName, message, diagnostic = {}) {
    const error = new Error(message);
    error.code = codeName;
    const stage = code(diagnostic.stage, 48);
    const status = Number(diagnostic.status);
    if (stage) error.stage = stage;
    if (Number.isInteger(status) && status >= 100 && status <= 599) error.status = status;
    return error;
  }

  function requireIdentifiers(payload) {
    const source = isObject(payload) ? payload : {};
    const schemaVersion = text(source.schema_version || source.schemaVersion, 20);
    const completionId = text(source.completion_id || source.completionId, 80);
    const dogId = text(source.dog_id || source.dogId, 120);
    if (schemaVersion !== SCHEMA_VERSION) throw providerError("invalid_payload", "The Raw Fit schema version is invalid.");
    if (!isUuid(completionId)) throw providerError("invalid_payload", "The Raw Fit completion ID must be a UUID.");
    if (!dogId) throw providerError("invalid_payload", "The Raw Fit dog ID is required.");
    return { schemaVersion, completionId, dogId };
  }

  function normalizedConditions(value) {
    if (!Array.isArray(value)) return [];
    return value.slice(0, 12).map((item) => {
      if (typeof item === "string") {
        const conditionCode = code(item);
        return conditionCode ? { code: conditionCode } : null;
      }
      if (!isObject(item)) return null;
      const conditionCode = code(item.code || item.condition || item.id);
      const timing = code(item.timing || item.state || item.status);
      if (!conditionCode) return null;
      return timing ? { code: conditionCode, timing } : { code: conditionCode };
    }).filter(Boolean);
  }

  function normalizedBoxQuantities(value) {
    if (!isObject(value)) return {};
    return Object.keys(value).reduce((quantities, rawSize) => {
      const labelledSize = /^box_(\d{1,2})_lb$/.exec(rawSize);
      const size = labelledSize ? labelledSize[1] : rawSize;
      if (!/^\d{1,2}$/.test(size)) return quantities;
      const quantity = finiteNumber(value[rawSize], 0, 99);
      if (quantity !== null && Number.isInteger(quantity)) quantities[size] = quantity;
      return quantities;
    }, {});
  }

  function normalizedSellingPlanIds(value) {
    if (!isObject(value)) return {};
    return [8, 14, 30].reduce((plans, size) => {
      const planId = displayText(value[size] ?? value[String(size)], 80);
      if (planId) plans[size] = planId;
      return plans;
    }, {});
  }

  function hasBoxQuantityKey(value, size) {
    if (!isObject(value)) return false;
    const sizeKey = String(size);
    if (own(value, sizeKey) || own(value, `box_${sizeKey}_lb`)) return true;
    const nested = value.box_quantities || value.boxQuantities;
    return isObject(nested) && (own(nested, sizeKey) || own(nested, `box_${sizeKey}_lb`));
  }

  function wholeNumber(value, min = 0, max = 365) {
    const number = finiteNumber(value, min, max);
    return number !== null && Number.isInteger(number) ? number : null;
  }

  function recommendedCadenceWeeks(value) {
    const cadence = wholeNumber(value, 2, 4);
    return cadence === 2 || cadence === 4 ? cadence : null;
  }

  function recommendedPurchaseMode(value) {
    return code(value) === "subscription" ? "subscription" : null;
  }

  function normalizedConditionTiming(value) {
    if (!isObject(value)) return null;
    return omitNull({
      status: code(value.status),
      wait: codeList(value.wait),
      conditional: codeList(value.conditional),
    });
  }

  function omitNull(object) {
    return Object.keys(object).reduce((result, key) => {
      const value = object[key];
      const keepEmptyArray = Array.isArray(value);
      const keepEmptyObject = isObject(value) && Object.keys(value).length > 0;
      if (value !== null && value !== undefined && value !== "" && (keepEmptyArray || keepEmptyObject || true)) result[key] = value;
      return result;
    }, {});
  }

  function consentEvidence(consent) {
    const source = isObject(consent) ? consent : {};
    return omitNull({
      source: code(source.source, 80),
      wording_version: text(source.wording_version || source.wordingVersion, 120) || null,
      timestamp: timestamp(source.timestamp || source.consent_timestamp || source.consentTimestamp),
    });
  }

  function hasEmailConsent(consent) {
    const source = isObject(consent) ? consent : {};
    const evidence = consentEvidence(source);
    return source.send_results === true
      && source.email_marketing === true
      && Boolean(evidence.source && evidence.wording_version && evidence.timestamp);
  }

  function hasSmsConsent(consent) {
    const source = isObject(consent) ? consent : {};
    return source.sms_marketing === true
      && Boolean(text(source.sms_wording_version || source.smsWordingVersion || source.wording_version || source.wordingVersion, 120))
      && Boolean(timestamp(source.sms_timestamp || source.smsTimestamp || source.timestamp || source.consent_timestamp || source.consentTimestamp));
  }

  function normalizeSnapshot(payload, identifiers, consent) {
    const source = isObject(payload?.completion_snapshot)
      ? payload.completion_snapshot
      : (isObject(payload?.properties?.completion_snapshot)
        ? payload.properties.completion_snapshot
        : (isObject(payload?.snapshot)
          ? payload.snapshot
          : (isObject(payload?.properties) ? payload.properties : null)));
    if (!source) throw providerError("invalid_payload", "A coded Raw Fit completion snapshot is required.");
    const answers = isObject(source.answers) ? source.answers : {};
    const dogSource = isObject(source.dog) ? source.dog : {};
    const resultSource = isObject(source.result) ? source.result : {};
    const feedingSource = isObject(source.feeding) ? source.feeding : (isObject(source.feeding_plan) ? source.feeding_plan : {});
    const orderSource = isObject(source.order) ? source.order : (isObject(source.order_plan) ? source.order_plan : {});
    const pricingSource = isObject(source.pricing) ? source.pricing : {};
    const dogSources = [dogSource, answers, source];
    const resultSources = [resultSource, source];
    const feedingSources = [feedingSource, source];
    const orderSources = [orderSource, source];

    const sourceSchema = text(source.schema_version || source.schemaVersion, 20);
    const sourceCompletionId = text(source.completion_id || source.completionId, 80);
    const sourceDogId = text(source.dog_id || source.dogId, 120);
    if (sourceSchema && sourceSchema !== identifiers.schemaVersion) throw providerError("invalid_payload", "The completion snapshot schema version does not match.");
    if (sourceCompletionId && sourceCompletionId !== identifiers.completionId) throw providerError("invalid_payload", "The completion snapshot completion ID does not match.");
    if (sourceDogId && sourceDogId !== identifiers.dogId) throw providerError("invalid_payload", "The completion snapshot dog ID does not match.");

    const birthdayMode = code(firstValue(dogSources, ["birthday_mode", "birthdayMode", "age_mode", "ageMode"]));
    const conditions = normalizedConditions(firstValue(dogSources, ["conditions", "condition_states", "conditionStates"]));
    const dog = omitNull({
      name: displayText(firstValue(dogSources, ["name", "dog_name", "dogName"])),
      dog_name: displayText(firstValue(dogSources, ["dog_name", "dogName", "name"])),
      birthday_mode: birthdayMode || code(firstValue(dogSources, ["birthday_precision", "birthdayPrecision"])),
      birthday: exactIsoDate(firstValue(dogSources, ["birthday", "birth_date", "birthDate"])),
      birthday_month: /^\d{4}-\d{2}$/.test(text(firstValue(dogSources, ["birthday_month", "birthdayMonth", "birth_month", "birthMonth"]), 7))
        ? text(firstValue(dogSources, ["birthday_month", "birthdayMonth", "birth_month", "birthMonth"]), 7)
        : null,
      estimated_age_months: finiteNumber(firstValue(dogSources, ["estimated_age_months", "estimatedAgeMonths", "age_months", "ageMonths"]), 0, 360),
      age_as_of: exactIsoDate(firstValue(dogSources, ["age_as_of", "ageAsOf"])),
      breed: displayText(firstValue(dogSources, ["breed", "breed_name", "breedName"])),
      weight_lb: finiteNumber(firstValue(dogSources, ["weight_lb", "weightLbs", "current_weight", "currentWeight", "current_weight_lb"]), 0.1, 400),
      weight_direction: code(firstValue(dogSources, ["weight_direction", "weightDirection", "weight_goal", "weightGoal", "goal"])),
      target_weight_lb: finiteNumber(firstValue(dogSources, ["target_weight_lb", "targetWeightLb", "target_weight", "targetWeight"]), 0.1, 400),
      activity: code(firstValue(dogSources, ["activity", "activity_level", "activityLevel"])),
      raw_experience: code(firstValue(dogSources, ["raw_experience", "rawExperience", "raw_status", "rawStatus"])),
      avoidances: codeList(firstValue(dogSources, ["avoidances", "protein_avoidances", "proteinAvoidances"])),
      tolerated_protein: code(firstValue(dogSources, ["tolerated_protein", "toleratedProtein", "protein_tolerance", "proteinTolerance"])),
      conditions,
      goals: codeList(firstValue(dogSources, ["goals", "outcomes", "desired_outcomes", "desiredOutcomes"])),
      primary_outcome: code(firstValue(dogSources, ["primary_outcome", "primaryOutcome", "outcome"])),
    });

    const result = omitNull({
      status: code(firstValue(resultSources, ["status", "feeding_status", "feedingStatus"])),
      reason_codes: codeList(firstValue(resultSources, ["reason_codes", "reasonCodes", "status_reason_codes", "statusReasonCodes", "status_reasons", "statusReasons"])),
      primary_recommendation: code(firstValue(resultSources, ["primary_recommendation", "primaryRecommendation", "recommendation"])),
      secondary_recommendation: code(firstValue(resultSources, ["secondary_recommendation", "secondaryRecommendation", "alternative"])),
      recommendation_route: code(firstValue(resultSources, ["recommendation_route", "recommendationRoute", "route"])),
      recommendation_reasons: displayList(firstValue(resultSources, ["recommendation_reasons", "recommendationReasons", "reasons"]), 3, 220),
      breed_notices: normalizedBreedNotices(firstValue(resultSources, ["breed_notices", "breedNotices"])),
      diet_watch_flags: codeList(firstValue(resultSources, ["diet_watch_flags", "dietWatchFlags"])),
      effective_avoidances: codeList(firstValue(resultSources, ["effective_avoidances", "effectiveAvoidances"])),
    });

    const feeding = omitNull({
      daily_ounces: finiteNumber(firstValue(feedingSources, ["daily_ounces", "dailyOunces"]), 0, 250),
      meals_per_day: finiteNumber(firstValue(feedingSources, ["meals_per_day", "mealsPerDay"]), 1, 3),
      per_meal_ounces: finiteNumber(firstValue(feedingSources, ["per_meal_ounces", "perMealOunces"]), 0, 250),
    });

    const requiredPounds28Days = finiteNumber(firstValue(orderSources, ["required_pounds_28_days", "requiredPounds28Days"]), 0, 1000);
    const suppliedDeliveredPounds = finiteNumber(firstValue(orderSources, ["total_ordered_pounds", "totalOrderedPounds", "delivered_pounds", "deliveredPounds"]), 0, 1000);
    const orderBoxQuantities = normalizedBoxQuantities(firstValue(orderSources, ["box_quantities", "boxQuantities"]) || orderSource);
    const hasCurrent14Quantity = orderSources.some((candidate) => hasBoxQuantityKey(candidate, 14));
    const hasHistorical16Quantity = !hasCurrent14Quantity
      && orderSources.some((candidate) => hasBoxQuantityKey(candidate, 16));
    const hasPackageRecommendation = requiredPounds28Days !== null
      || suppliedDeliveredPounds !== null
      || Object.values(orderBoxQuantities).some((quantity) => quantity > 0);
    const calculatedDifference = requiredPounds28Days !== null && suppliedDeliveredPounds !== null
      ? suppliedDeliveredPounds - requiredPounds28Days
      : null;
    const order = omitNull({
      required_pounds_28_days: requiredPounds28Days,
      box_quantities: orderBoxQuantities,
      total_ordered_pounds: suppliedDeliveredPounds,
      delivered_pounds: suppliedDeliveredPounds,
      coverage_days: finiteNumber(firstValue(orderSources, ["coverage_days", "coverageDays"]), 0, 365),
      buffer_pounds: finiteNumber(firstValue(orderSources, ["buffer_pounds", "bufferPounds"]), 0, 1000),
      overage_pounds: calculatedDifference !== null
        ? Math.max(0, calculatedDifference)
        : finiteNumber(firstValue(orderSources, ["overage_pounds", "overagePounds"]), 0, 1000),
      shortfall_pounds: calculatedDifference !== null
        ? Math.max(0, calculatedDifference * -1)
        : finiteNumber(firstValue(orderSources, ["shortfall_pounds", "shortfallPounds"]), 0, 1000),
      days_not_covered: wholeNumber(firstValue(orderSources, ["days_not_covered", "daysNotCovered"]), 0, 365),
      recommended_cadence_weeks: recommendedCadenceWeeks(firstValue(orderSources, ["recommended_cadence_weeks", "recommendedCadenceWeeks"])),
      // The email capture precedes purchase controls. Do not preserve a
      // customer-selected one-time/cadence value as completion data.
      recommended_purchase_mode: hasPackageRecommendation
        ? (recommendedPurchaseMode(firstValue(orderSources, ["recommended_purchase_mode", "recommendedPurchaseMode"])) || "subscription")
        : null,
    });

    const pricing = omitNull({
      currency: /^[A-Z]{3}$/.test(text(pricingSource.currency, 3).toUpperCase()) ? text(pricingSource.currency, 3).toUpperCase() : null,
      one_time_price_cents: finiteNumber(pricingSource.one_time_price_cents ?? pricingSource.oneTimePriceCents, 0, 10000000),
      subscription_price_cents: finiteNumber(pricingSource.subscription_price_cents ?? pricingSource.subscriptionPriceCents, 0, 10000000),
      subscription_savings_cents: finiteNumber(pricingSource.subscription_savings_cents ?? pricingSource.savingsCents, 0, 10000000),
      subscription_savings_percent: finiteNumber(pricingSource.subscription_savings_percent ?? pricingSource.savingsPercent, 0, 100),
    });

    const orderPlan = omitNull({
      box_8_lb: order.box_quantities?.[8] ?? 0,
      ...(hasHistorical16Quantity
        ? { box_16_lb: order.box_quantities?.[16] ?? 0 }
        : {
          box_14_lb: order.box_quantities?.[14] ?? 0,
          // New 3.0 payloads reserve this historical field as zero; 14 is never
          // represented as 16 during the flow-template compatibility period.
          box_16_lb: 0,
        }),
      box_30_lb: order.box_quantities?.[30] ?? 0,
      box_quantities: order.box_quantities,
      total_ordered_pounds: order.total_ordered_pounds,
      delivered_pounds: order.delivered_pounds,
      coverage_days: order.coverage_days,
      buffer_pounds: order.buffer_pounds,
      overage_pounds: order.overage_pounds,
      shortfall_pounds: order.shortfall_pounds,
      days_not_covered: order.days_not_covered,
      recommended_cadence_weeks: order.recommended_cadence_weeks,
      recommended_purchase_mode: order.recommended_purchase_mode,
    });

    const submittedAt = timestamp(source.submitted_at || source.submittedAt || source.completed_at || source.completedAt || payload.submitted_at || payload.submittedAt) || consentEvidence(consent).timestamp || new Date().toISOString();
    const conditionTiming = normalizedConditionTiming(source.condition_timing || source.conditionTiming);

    return omitNull({
      schema_version: identifiers.schemaVersion,
      quiz_version: displayText(source.quiz_version, 20),
      calculation_revision: displayText(source.calculation_revision, 120),
      completion_id: identifiers.completionId,
      dog_id: identifiers.dogId,
      submitted_at: submittedAt,
      completed_at: submittedAt,
      dog,
      result,
      feeding,
      order,
      dog_name: dog.dog_name || dog.name,
      dog_breed: dog.breed,
      birthday_precision: dog.birthday_mode,
      birthday: dog.birthday,
      birthday_month: dog.birthday_month,
      estimated_age_months: dog.estimated_age_months,
      age_as_of: dog.age_as_of,
      current_weight_lb: dog.weight_lb,
      weight_goal: dog.weight_direction,
      target_weight_lb: dog.target_weight_lb,
      activity: dog.activity,
      raw_experience: dog.raw_experience,
      avoidances: dog.avoidances,
      tolerated_protein: dog.tolerated_protein,
      tolerated_proteins: dog.tolerated_protein ? [dog.tolerated_protein] : [],
      conditions: dog.conditions,
      condition_timing: conditionTiming,
      primary_outcome: dog.primary_outcome,
      outcomes: dog.goals,
      feeding_status: result.status,
      status_reason_codes: result.reason_codes,
      recommendation_route: result.recommendation_route,
      primary_recommendation: result.primary_recommendation,
      secondary_recommendation: result.secondary_recommendation,
      recommendation_reasons: result.recommendation_reasons,
      breed_notices: result.breed_notices,
      diet_watch_flags: result.diet_watch_flags,
      effective_avoidances: result.effective_avoidances,
      transition_plan: normalizedTransitionPlan(source.transition_plan),
      daily_ounces: feeding.daily_ounces,
      meals_per_day: feeding.meals_per_day,
      per_meal_ounces: feeding.per_meal_ounces,
      required_pounds_28_days: order.required_pounds_28_days,
      order_plan: orderPlan,
      pricing,
      recommended_cadence_weeks: order.recommended_cadence_weeks,
      recommended_purchase_mode: order.recommended_purchase_mode,
      purchase_type: order.recommended_purchase_mode,
      selling_plan_id: displayText(source.selling_plan_id || source.sellingPlanId, 80),
      selling_plan_ids: normalizedSellingPlanIds(source.selling_plan_ids || source.sellingPlanIds),
      selling_plan_cadence_weeks: finiteNumber(source.selling_plan_cadence_weeks ?? source.sellingPlanCadenceWeeks, 1, 52),
      subscription_plan_status: code(source.subscription_plan_status || source.subscriptionPlanStatus),
      consent: consentEvidence(consent),
    });
  }

  function deepClone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function deepFreeze(value) {
    if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
    Object.freeze(value);
    Object.keys(value).forEach((key) => deepFreeze(value[key]));
    return value;
  }

  function buildEmailSubmission(payload) {
    const identifiers = requireIdentifiers(payload);
    const consent = isObject(payload?.consent) ? payload.consent : {};
    const email = normalizeEmail(payload?.email);
    if (!email) throw providerError("invalid_payload", "A valid email is required for Raw Fit delivery.");
    return {
      ...identifiers,
      email,
      humanFirstName: displayText(payload?.human_first_name || payload?.humanFirstName || payload?.completion_snapshot?.human_first_name || payload?.properties?.completion_snapshot?.human_first_name || payload?.properties?.human_first_name),
      consent,
      snapshot: deepFreeze(deepClone(normalizeSnapshot(payload, identifiers, consent))),
    };
  }

  function buildSmsSubmission(payload) {
    const identifiers = requireIdentifiers(payload);
    const consent = isObject(payload?.consent) ? payload.consent : {};
    const phone = normalizePhone(payload?.phone || payload?.phone_number || payload?.phoneNumber);
    if (!phone) throw providerError("invalid_payload", "A valid SMS phone number is required.");
    return {
      ...identifiers,
      phone,
      consent,
    };
  }

  function smsProfileProperties(submission) {
    const consent = isObject(submission.consent) ? submission.consent : {};
    return omitNull({
      phone_number: submission.phone,
      "RD SMS Marketing Consent Source": code(consent.source, 80),
      "RD SMS Marketing Consent Wording": text(consent.sms_wording_version || consent.smsWordingVersion || consent.wording_version || consent.wordingVersion, 120),
      "RD SMS Marketing Consent At": timestamp(consent.sms_timestamp || consent.smsTimestamp || consent.timestamp || consent.consent_timestamp || consent.consentTimestamp),
    });
  }

  function conditionLabels(conditions) {
    return conditions.map((condition) => (condition.timing ? `${condition.code}:${condition.timing}` : condition.code));
  }

  function profileProperties(submission, extras) {
    const snapshot = submission.snapshot || {};
    const dog = snapshot.dog || {};
    const result = snapshot.result || {};
    const feeding = snapshot.feeding || {};
    const order = snapshot.order || {};
    const pricing = snapshot.pricing || {};
    const conditionTiming = snapshot.condition_timing || {};
    const consent = consentEvidence(submission.consent);
    const properties = omitNull({
      email: submission.email,
      first_name: submission.humanFirstName,
      phone_number: submission.phone,
      "RD Quiz Source": "Raw Fit Quiz",
      "RD Quiz Schema Version": submission.schemaVersion,
      "RD Quiz Completion ID": submission.completionId,
      "RD Current Dog ID": submission.dogId,
      "RD Current Dog Name": dog.name || null,
      "RD Current Birthday Mode": dog.birthday_mode || null,
      "RD Current Birthday": dog.birthday || null,
      "RD Current Birthday Month": dog.birthday_month || null,
      "RD Current Estimated Age Months": dog.estimated_age_months,
      "RD Current Age As Of": dog.age_as_of || null,
      "RD Current Breed": dog.breed || null,
      "RD Current Weight Lb": dog.weight_lb,
      "RD Current Weight Direction": dog.weight_direction || null,
      "RD Current Target Weight Lb": dog.target_weight_lb,
      "RD Current Activity": dog.activity || null,
      "RD Current Raw Experience": dog.raw_experience || null,
      "RD Current Avoidances": dog.avoidances || [],
      "RD Current Tolerated Protein": dog.tolerated_protein || null,
      "RD Current Conditions": conditionLabels(dog.conditions || []),
      "RD Current Condition Status": conditionTiming.status || null,
      "RD Current Wait Conditions": conditionTiming.wait || [],
      "RD Current Conditional Conditions": conditionTiming.conditional || [],
      "RD Current Goals": dog.goals || [],
      "RD Current Primary Outcome": dog.primary_outcome || null,
      "RD Current Feeding Status": result.status || null,
      "RD Current Status Reason Codes": result.reason_codes || [],
      "RD Current Primary Recommendation": result.primary_recommendation || null,
      "RD Current Secondary Recommendation": result.secondary_recommendation || null,
      "RD Current Recommendation Route": result.recommendation_route || null,
      "RD Current Daily Ounces": feeding.daily_ounces,
      "RD Current Meals Per Day": feeding.meals_per_day,
      "RD Current Per Meal Ounces": feeding.per_meal_ounces,
      "RD Current 28 Day Pounds": order.required_pounds_28_days,
      "RD Current Box Quantities": Object.keys(order.box_quantities || {}).length ? JSON.stringify(order.box_quantities) : null,
      "RD Current Order Pounds": order.total_ordered_pounds,
      "RD Current Coverage Days": order.coverage_days,
      "RD Current Buffer Pounds": order.buffer_pounds,
      "RD Current Delivered Pounds": order.delivered_pounds,
      "RD Current Overage Pounds": order.overage_pounds,
      "RD Current Shortfall Pounds": order.shortfall_pounds,
      "RD Current Days Not Covered": order.days_not_covered,
      "RD Current Recommended Cadence Weeks": order.recommended_cadence_weeks,
      "RD Current Recommended Purchase Mode": order.recommended_purchase_mode || null,
      "RD Current Purchase Mode": order.recommended_purchase_mode || null,
      "RD Current Currency": pricing.currency || null,
      "RD Current One Time Price Cents": pricing.one_time_price_cents,
      "RD Current Subscription Price Cents": pricing.subscription_price_cents,
      "RD Current Subscription Savings Cents": pricing.subscription_savings_cents,
      "RD Current Subscription Savings Percent": pricing.subscription_savings_percent,
      "RD Current Selling Plan ID": snapshot.selling_plan_id || null,
      "RD Current Selling Plan IDs": Object.keys(snapshot.selling_plan_ids || {}).length ? JSON.stringify(snapshot.selling_plan_ids) : null,
      "RD Current Selling Plan Cadence Weeks": snapshot.selling_plan_cadence_weeks,
      "RD Current Subscription Plan Status": snapshot.subscription_plan_status || null,
      "RD Email Marketing Consent Source": consent.source || null,
      "RD Email Marketing Consent Wording": consent.wording_version || null,
      "RD Email Marketing Consent At": consent.timestamp || null,
      "RD Quiz Completed At": snapshot.submitted_at || null,
      ...extras,
    });
    [
      "RD Current Dog ID",
      "RD Current Dog Name",
      "RD Current Birthday Mode",
      "RD Current Birthday",
      "RD Current Birthday Month",
      "RD Current Estimated Age Months",
      "RD Current Age As Of",
      "RD Current Breed",
      "RD Current Weight Lb",
      "RD Current Weight Direction",
      "RD Current Target Weight Lb",
      "RD Current Activity",
      "RD Current Raw Experience",
      "RD Current Avoidances",
      "RD Current Tolerated Protein",
      "RD Current Conditions",
      "RD Current Condition Status",
      "RD Current Wait Conditions",
      "RD Current Conditional Conditions",
      "RD Current Goals",
      "RD Current Primary Outcome",
      "RD Current Feeding Status",
      "RD Current Status Reason Codes",
      "RD Current Primary Recommendation",
      "RD Current Secondary Recommendation",
      "RD Current Recommendation Route",
      "RD Current Daily Ounces",
      "RD Current Meals Per Day",
      "RD Current Per Meal Ounces",
      "RD Current 28 Day Pounds",
      "RD Current Box Quantities",
      "RD Current Order Pounds",
      "RD Current Coverage Days",
      "RD Current Buffer Pounds",
      "RD Current Delivered Pounds",
      "RD Current Overage Pounds",
      "RD Current Shortfall Pounds",
      "RD Current Days Not Covered",
      "RD Current Recommended Cadence Weeks",
      "RD Current Recommended Purchase Mode",
      "RD Current Purchase Mode",
      "RD Current Currency",
      "RD Current One Time Price Cents",
      "RD Current Subscription Price Cents",
      "RD Current Subscription Savings Cents",
      "RD Current Subscription Savings Percent",
      "RD Current Selling Plan ID",
      "RD Current Selling Plan IDs",
      "RD Current Selling Plan Cadence Weeks",
      "RD Current Subscription Plan Status",
    ].forEach((property) => {
      if (!Object.prototype.hasOwnProperty.call(properties, property)) properties[property] = null;
    });
    return properties;
  }

  function completionEventProperties(submission) {
    return deepFreeze({
      $event_id: submission.completionId,
      schema_version: submission.schemaVersion,
      completion_id: submission.completionId,
      dog_id: submission.dogId,
      completion_snapshot: submission.snapshot,
    });
  }

  function planEventProperties(submission) {
    return deepFreeze({
      $event_id: submission.completionId,
      schema_version: submission.schemaVersion,
      completion_id: submission.completionId,
      dog_id: submission.dogId,
      plan_requested_at: new Date().toISOString(),
      consent: consentEvidence(submission.consent),
      completion_snapshot: submission.snapshot,
    });
  }

  function smsEventProperties(submission) {
    return deepFreeze({
      $event_id: submission.completionId,
      schema_version: submission.schemaVersion,
      completion_id: submission.completionId,
      dog_id: submission.dogId,
      sms_consent: {
        wording_version: text(submission.consent.sms_wording_version || submission.consent.smsWordingVersion || submission.consent.wording_version || submission.consent.wordingVersion, 120),
        timestamp: timestamp(submission.consent.sms_timestamp || submission.consent.smsTimestamp || submission.consent.timestamp || submission.consent.consent_timestamp || submission.consent.consentTimestamp),
      },
    });
  }

  function clientStorage() {
    try {
      return window.localStorage || null;
    } catch (_error) {
      return null;
    }
  }

  function emptyDeliveryState() {
    const started = new Date();
    return {
      version: 3,
      created_at: started.toISOString(),
      expires_at: new Date(started.getTime() + RETRY_TTL_MS).toISOString(),
      profile_identified: false,
      email_subscribed: false,
      completion_tracked: false,
      plan_tracked: false,
      sms_profile_identified: false,
      sms_subscribed: false,
      sms_tracked: false,
      retry: null,
    };
  }

  function deliveryState(completionId) {
    const store = clientStorage();
    const key = `${STORAGE_PREFIX}${completionId}`;
    let saved = null;
    try {
      saved = JSON.parse(store?.getItem(key) || "null");
    } catch (_error) {
      saved = null;
    }
    const expired = !isObject(saved) || !timestamp(saved.expires_at) || Date.parse(saved.expires_at) <= Date.now();
    if (expired) {
      try { store?.removeItem(key); } catch (_error) { /* short-lived retry persistence is optional */ }
      saved = emptyDeliveryState();
    }
    return {
      key,
      store,
      state: {
        version: 3,
        created_at: timestamp(saved.created_at) || new Date().toISOString(),
        expires_at: timestamp(saved.expires_at) || new Date(Date.now() + RETRY_TTL_MS).toISOString(),
        profile_identified: saved.profile_identified === true,
        email_subscribed: saved.email_subscribed === true,
        completion_tracked: saved.completion_tracked === true,
        plan_tracked: saved.plan_tracked === true,
        sms_profile_identified: saved.sms_profile_identified === true,
        sms_subscribed: saved.sms_subscribed === true,
        sms_tracked: saved.sms_tracked === true,
        retry: isObject(saved.retry) ? {
          event: text(saved.retry.event, 80) || null,
          reason: code(saved.retry.reason, 80) || null,
          stage: code(saved.retry.stage, 48) || null,
          status: Number.isInteger(saved.retry.status) && saved.retry.status >= 100 && saved.retry.status <= 599 ? saved.retry.status : null,
          at: timestamp(saved.retry.at),
        } : null,
      },
    };
  }

  function persist(delivery) {
    try {
      delivery.store?.setItem(delivery.key, JSON.stringify(delivery.state));
    } catch (_error) {
      // Results remain usable if a browser blocks storage.
    }
  }

  function klaviyoEmailClient() {
    const client = window.klaviyo;
    if (!client || typeof client.identify !== "function") {
      throw providerError("unavailable", "Klaviyo is not available.", { stage: "client_init" });
    }
    return client;
  }

  function klaviyoSmsClient() {
    const client = klaviyoEmailClient();
    if (typeof client.track !== "function") {
      throw providerError("unavailable", "Klaviyo is not available.", { stage: "client_init" });
    }
    return client;
  }

  async function withTimeout(operation, stage) {
    let timer = null;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(providerError("timeout", "The Klaviyo request timed out.", { stage })), REQUEST_TIMEOUT_MS);
    });
    try {
      return await Promise.race([Promise.resolve().then(operation), timeout]);
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }

  async function clientCall(client, stage, method, ...args) {
    let result;
    try {
      result = await withTimeout(() => client[method](...args), stage);
    } catch (error) {
      if (error?.code) throw error;
      throw providerError("sdk_failure", "Klaviyo did not complete the request.", { stage });
    }
    if (result === false) throw providerError("rejected", `Klaviyo ${method} rejected the request.`, { stage });
    // The storefront SDK may acknowledge an accepted browser queue with no
    // return value. That is an acknowledgement of dispatch, not proof that an
    // email has been delivered.
    return result;
  }

  function fetchRequest(stage) {
    if (typeof window.fetch === "function") return window.fetch.bind(window);
    if (typeof fetch === "function") return fetch;
    throw providerError("unavailable", "Klaviyo consent delivery is not available.", { stage });
  }

  async function subscribe(submission, channel) {
    const profileAttributes = channel === "email"
      ? {
        email: submission.email,
        subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },
      }
      : {
        phone_number: submission.phone,
        subscriptions: { sms: { marketing: { consent: "SUBSCRIBED" } } },
      };
    let response;
    try {
      response = await withTimeout(() => fetchRequest("subscribe")("https://a.klaviyo.com/client/subscriptions?company_id=" + encodeURIComponent(PUBLIC_SITE_ID), {
        method: "POST",
        headers: {
          "content-type": "application/vnd.api+json",
          revision: API_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: channel === "email" ? "Raw Fit Quiz V3" : "Raw Fit Quiz V3 SMS",
              profile: { data: { type: "profile", attributes: profileAttributes } },
            },
            relationships: { list: { data: { type: "list", id: MARKETING_LIST_ID } } },
          },
        }),
      }), "subscribe");
    } catch (error) {
      if (error?.code) throw error;
      throw providerError("network", "Klaviyo consent delivery failed.", { stage: "subscribe" });
    }
    if (Number(response?.status) !== 202) {
      throw providerError("rejected", "Klaviyo did not accept the consent request.", { stage: "subscribe", status: response?.status });
    }
  }

  async function createClientEvent(eventName, properties, submission, stage) {
    let response;
    try {
      response = await withTimeout(() => fetchRequest(stage)("https://a.klaviyo.com/client/events?company_id=" + encodeURIComponent(PUBLIC_SITE_ID), {
        method: "POST",
        headers: {
          "content-type": "application/vnd.api+json",
          revision: API_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: "event",
            attributes: {
              properties,
              unique_id: submission.completionId,
              metric: {
                data: {
                  type: "metric",
                  attributes: { name: eventName },
                },
              },
              profile: {
                data: {
                  type: "profile",
                  attributes: { email: submission.email },
                },
              },
            },
          },
        }),
      }), stage);
    } catch (error) {
      if (error?.code) throw error;
      throw providerError("network", "Klaviyo event delivery failed.", { stage });
    }
    if (Number(response?.status) !== 202) {
      throw providerError("rejected", "Klaviyo did not accept the event.", { stage, status: response?.status });
    }
  }

  function errorReason(error) {
    if (error?.code === "timeout") return "provider_timeout";
    if (error?.code === "unavailable") return "provider_unavailable";
    if (error?.code === "network") return "provider_network";
    return "provider_rejected";
  }

  function denied(event, identifiers) {
    return {
      acknowledged: false,
      retryable: false,
      completion_id: identifiers.completionId,
      dog_id: identifiers.dogId,
      event,
      reason: "explicit_consent_required",
    };
  }

  function failed(event, submission, delivery, error) {
    const reason = errorReason(error);
    const stage = code(error?.stage, 48) || "unknown";
    const status = Number.isInteger(error?.status) && error.status >= 100 && error.status <= 599 ? error.status : null;
    delivery.state.retry = { event, reason, stage, status, at: new Date().toISOString() };
    persist(delivery);
    return {
      acknowledged: false,
      retryable: true,
      completion_id: submission.completionId,
      dog_id: submission.dogId,
      event,
      retry: {
        event,
        reason,
        stage,
        status,
        expires_at: delivery.state.expires_at,
      },
      message: "We could not verify your request with our email provider. Your quiz results are still available here. Please try again.",
    };
  }

  function acknowledged(event, submission, delivery, duplicate) {
    delivery.state.retry = null;
    persist(delivery);
    return {
      acknowledged: true,
      duplicate: duplicate === true,
      completion_id: submission.completionId,
      dog_id: submission.dogId,
      event,
      delivery_state: "provider_acknowledged",
    };
  }

  async function identifyEmail(client, submission, delivery) {
    if (delivery.state.profile_identified) return;
    await clientCall(client, "identify", "identify", profileProperties(submission));
    delivery.state.profile_identified = true;
    persist(delivery);
  }

  async function subscribeEmail(submission, delivery) {
    if (delivery.state.email_subscribed) return;
    await subscribe(submission, "email");
    delivery.state.email_subscribed = true;
    persist(delivery);
  }

  async function trackCompletion(submission, delivery) {
    if (delivery.state.completion_tracked) return;
    await createClientEvent("Raw Fit Quiz Completed", completionEventProperties(submission), submission, "completion_track");
    delivery.state.completion_tracked = true;
    persist(delivery);
  }

  async function trackPlan(submission, delivery) {
    if (delivery.state.plan_tracked) return;
    await createClientEvent("Raw Fit Plan Requested", planEventProperties(submission), submission, "plan_track");
    delivery.state.plan_tracked = true;
    persist(delivery);
  }

  async function deliverEmailEvent(event, payload) {
    const submission = buildEmailSubmission(payload);
    if (!hasEmailConsent(submission.consent)) return denied(event, submission);
    const delivery = deliveryState(submission.completionId);
    const alreadyTracked = event === "Raw Fit Quiz Completed" ? delivery.state.completion_tracked : delivery.state.plan_tracked;
    if (alreadyTracked) return acknowledged(event, submission, delivery, true);
    try {
      const client = klaviyoEmailClient();
      await identifyEmail(client, submission, delivery);
      await subscribeEmail(submission, delivery);
      await trackCompletion(submission, delivery);
      if (event === "Raw Fit Plan Requested") await trackPlan(submission, delivery);
      return acknowledged(event, submission, delivery, false);
    } catch (error) {
      return failed(event, submission, delivery, error);
    }
  }

  async function deliverSmsEvent(payload) {
    const submission = buildSmsSubmission(payload);
    const event = "Raw Fit SMS Opt-In";
    if (!hasSmsConsent(submission.consent)) return denied(event, submission);
    const delivery = deliveryState(submission.completionId);
    if (delivery.state.sms_tracked) return acknowledged(event, submission, delivery, true);
    try {
      const client = klaviyoSmsClient();
      if (!delivery.state.sms_profile_identified) {
        await clientCall(client, "sms_identify", "identify", smsProfileProperties(submission));
        delivery.state.sms_profile_identified = true;
        persist(delivery);
      }
      if (!delivery.state.sms_subscribed) {
        await subscribe(submission, "sms");
        delivery.state.sms_subscribed = true;
        persist(delivery);
      }
      await clientCall(client, "sms_track", "track", event, smsEventProperties(submission));
      delivery.state.sms_tracked = true;
      persist(delivery);
      return acknowledged(event, submission, delivery, false);
    } catch (error) {
      return failed(event, submission, delivery, error);
    }
  }

  window.RawDawgQuizEventProvider = async ({ event, payload } = {}) => {
    if (!SUPPORTED_EVENTS.has(event)) throw providerError("invalid_event", "Unsupported quiz event.");
    if (event === "Raw Fit SMS Opt-In") return deliverSmsEvent(payload);
    return deliverEmailEvent(event, payload);
  };
})();
